import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isOwner, useApp } from '../lib/store'
import { pendingCount, pushNow } from '../lib/sync'
import { AppBar } from '../components/AppBar'
import { Sheet } from '../components/Sheet'
import { BANKS } from '../lib/vietqr'
import { SKINS } from '../lib/skin'

export function SettingsScreen() {
  const nav = useNavigate()
  const shop = useApp((s) => s.shop)
  const session = useApp((s) => s.session)
  const owner = isOwner(session?.role)
  const signOut = useApp((s) => s.signOut)
  const skin = useApp((s) => s.skin)
  const setSkin = useApp((s) => s.setSkin)

  const bank = BANKS.find((b) => b.bin === shop?.bankBin)

  /*
   * Đăng xuất mà không bỏ sót đơn nào.
   *
   * Mọi thao tác đều ghi vào kho dữ liệu — nhưng kho ấy nằm trên <b>chính cái máy này</b>,
   * và nó chỉ tới được máy chủ ở lần đồng bộ kế tiếp. Quán ghi đơn lúc rớt mạng thì đơn nằm
   * đó chờ, đăng xuất là xoá sạch máy nên nó không còn ở đâu nữa.
   *
   * Nên trước khi xoá thì đẩy nốt đã. Đẩy xong mà vẫn còn treo — thường là vẫn chưa có mạng
   * — thì hỏi lại kèm con số cụ thể, chứ không doạ chung chung rồi để người ta tự đoán.
   */
  const [leaving, setLeaving] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  async function leave() {
    setBusy(true)
    await pushNow()
    const left = await pendingCount()
    setBusy(false)

    if (left > 0) {
      setLeaving(left)
      return
    }
    await signOut()
    nav('/')
  }

  async function leaveAnyway() {
    setLeaving(null)
    await signOut()
    nav('/')
  }

  return (
    <div className="screen">
      <AppBar title="Cài đặt" sub={shop?.name} onBack={() => nav('/')} />

      <div className="body">
        <h2 className="section-label">Quán</h2>
        <div className="list">
          {!owner && (
            <div className="row-btn" style={{ cursor: 'default' }}>
              <span className="row-icon" aria-hidden="true">
                🧑‍🍳
              </span>
              <span className="row-text">
                <b>{session?.name ?? 'Người bán hàng'}</b>
                <small>Bán hàng · không xem được doanh thu và thuế</small>
              </span>
            </div>
          )}

          {owner && (
            <>
          <button type="button" className="row-btn" onClick={() => nav('/settings/shop')}>
            <span className="row-icon" aria-hidden="true">
              🏠
            </span>
            <span className="row-text">
              <b>Thông tin quán</b>
              <small>
                {shop?.taxCode
                  ? `${shop.name} · MST ${shop.taxCode}`
                  : `${shop?.name ?? ''} · chưa khai mã số thuế`}
              </small>
            </span>
            <span aria-hidden="true">›</span>
          </button>

          <button type="button" className="row-btn" onClick={() => nav('/settings/bank')}>
            <span className="row-icon" aria-hidden="true">
              🏦
            </span>
            <span className="row-text">
              <b>Tài khoản nhận chuyển khoản</b>
              <small>
                {shop?.bankAccountNo ? `${bank?.short ?? ''} · ${shop.bankAccountNo}` : 'Chưa khai — chưa hiện được mã QR'}
              </small>
            </span>
            <span aria-hidden="true">›</span>
          </button>

          <button type="button" className="row-btn" onClick={() => nav('/settings/products')}>
            <span className="row-icon" aria-hidden="true">
              📋
            </span>
            <span className="row-text">
              <b>Sản phẩm</b>
              <small>Thêm, sửa giá, gán mã vạch</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>

          <button type="button" className="row-btn" onClick={() => nav('/settings/promotions')}>
            <span className="row-icon" aria-hidden="true">
              🎁
            </span>
            <span className="row-text">
              <b>Khuyến mãi</b>
              <small>Lập sẵn chương trình giảm giá để người bán bấm một cái</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>

          <button type="button" className="row-btn" onClick={() => nav('/settings/einvoice')}>
            <span className="row-icon" aria-hidden="true">
              🧾
            </span>
            <span className="row-text">
              <b>Hoá đơn điện tử</b>
              <small>
                {shop?.eInvoiceEnabled
                  ? shop.eInvoiceAutoIssue
                    ? 'Đang bật · tự xuất mọi đơn'
                    : 'Đang bật · xuất khi khách xin'
                  : 'Chưa bật — bán hàng vẫn in bill bình thường'}
              </small>
            </span>
            <span aria-hidden="true">›</span>
          </button>

          <button type="button" className="row-btn" onClick={() => nav('/settings/staff')}>
            <span className="row-icon" aria-hidden="true">
              🧑‍🍳
            </span>
            <span className="row-text">
              <b>Người làm</b>
              <small>Thêm người bán, phân quyền</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>
            </>
          )}

          <button type="button" className="row-btn" onClick={() => nav('/settings/printer')}>
            <span className="row-icon" aria-hidden="true">
              🖨
            </span>
            <span className="row-text">
              <b>Máy in bill</b>
              <small>Nối qua sigoPOS Print Agent</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        </div>

        {/* Sổ nợ khách quen: ai cũng mở được để tra và thu tiền, chỉ chủ quán mới thêm
            người mới — cho nợ là quyết định của người bỏ vốn. */}
        <h2 className="section-label">Khách quen</h2>
        <button type="button" className="row-btn" onClick={() => nav('/settings/customers')}>
          <span className="row-icon" aria-hidden="true">
            📒
          </span>
          <span className="row-text">
            <b>Sổ nợ khách quen</b>
            <small>Khách mang đồ về trước, tiền trả sau — xem ai còn nợ bao nhiêu</small>
          </span>
          <span aria-hidden="true">›</span>
        </button>

        {/* Tiền thuê bao đứng trên sở thích giao diện: hết hạn là quán ngừng bán được,
            còn chọn Modern hay Classic thì chỉ là chọn cho vừa tay. */}
        {owner && (
          <>
            <h2 className="section-label">Gói dịch vụ</h2>
            <button type="button" className="row-btn" onClick={() => nav('/settings/billing')}>
              <span className="row-icon" aria-hidden="true">
                💳
              </span>
              <span className="row-text">
                <b>{shop?.plan === 'Free' ? 'Đang dùng gói miễn phí' : 'Gói Quán nhỏ'}</b>
                <small>
                  {shop?.plan === 'Free'
                    ? '30 sản phẩm · 50 hoá đơn/ngày · không có báo cáo thuế'
                    : 'Xem hạn dùng và gia hạn'}
                </small>
              </span>
              <span aria-hidden="true">›</span>
            </button>
          </>
        )}

        {/*
          * Kiểu giao diện là sở thích của cái máy này, không phải của quán — nên ai đứng
          * bán ở máy nào cũng tự đổi được, không cần quyền chủ quán. Máy quầy nằm ngang
          * hợp bộ Modern, điện thoại cầm một tay hợp bộ Classic.
          */}
        <h2 className="section-label">Giao diện</h2>
        <div className="skin-pick">
          {SKINS.map((s) => (
            <button
              key={s.id}
              type="button"
              className="skin-card"
              aria-pressed={skin === s.id}
              onClick={() => setSkin(s.id)}
            >
              <span className={`skin-shot is-${s.id}`} aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              <b>{s.label}</b>
              <small>{s.hint}</small>
            </button>
          ))}
        </div>

        <button
          type="button"
          className="btn btn-danger-ghost wide"
          disabled={busy}
          onClick={() => void leave()}
        >
          {busy ? 'Đang gửi nốt đơn…' : 'Đăng xuất khỏi máy này'}
        </button>
        <p className="note wide">
          Đăng xuất là xoá sạch dữ liệu trên máy này, lần đăng nhập sau tải lại từ máy chủ. Đơn
          đã đồng bộ thì an toàn. Đơn ghi lúc rớt mạng mà chưa kịp gửi lên thì không có ở đâu
          khác, nên máy sẽ tự đẩy nốt trước khi đăng xuất — còn treo lại thì hỏi bạn.
        </p>
      </div>

      {leaving !== null && (
        <Sheet title="Còn đơn chưa gửi lên" onClose={() => setLeaving(null)}>
          <p className="sheet-note">
            Máy này còn <b>{leaving}</b> đơn chưa lên tới máy chủ, thường là vì chưa có mạng.
            Đăng xuất bây giờ là mất hẳn mấy đơn đó, không lấy lại được.
          </p>

          <div className="sheet-actions">
            <button type="button" className="btn btn-brand grow" onClick={() => setLeaving(null)}>
              Để đó, chờ có mạng
            </button>
            <button
              type="button"
              className="btn btn-danger-ghost"
              onClick={() => void leaveAnyway()}
            >
              Vẫn đăng xuất
            </button>
          </div>
        </Sheet>
      )}
    </div>
  )
}
